/**
 * Width-constrained content wrapper. Ported from ThemeMaker's
 * {@code blocks/layout/Container/Container.server.tsx}.
 *
 * <p>The {@code content} slot is pre-rendered to a {@code ReactNode}
 * by the renderer before this component runs.
 */
import type { ReactNode } from 'react';
import { BlockRoot } from '../_shared/BlockRoot';

const containerWidthMap: Record<string, string> = {
  narrow: '640px',
  default: '1200px',
  wide: '1440px',
  full: '100%',
};

export interface ContainerProps {
  width?: keyof typeof containerWidthMap;
  align?: 'left' | 'center' | 'right';
  /** Pre-rendered ReactNode — resolved by RenderOneBlock before this runs. */
  content?: ReactNode;
}

export function Container({ width = 'default', align = 'left', content }: ContainerProps) {
  return (
    <BlockRoot
      as="div"
      style={{
        maxWidth: containerWidthMap[width] ?? containerWidthMap['default'],
        margin: '0 auto',
        padding: '0 var(--space-6, 24px)',
        textAlign: align,
        // Own stacking context so a nested BackgroundGlow (z-index:-1)
        // sits above this container's background yet behind its content.
        position: 'relative',
        isolation: 'isolate',
      }}
    >
      {content}
    </BlockRoot>
  );
}
